import React from 'react'
import { Search } from 'lucide-react'

interface SearchResult {
  icon?: React.ReactNode
  name: string
  type: string
  [key: string]: unknown
}

interface SidebarSearchProps {
  value: string
  onChange: (value: string) => void
  results: SearchResult[]
  showDropdown: boolean
  setShowDropdown: (show: boolean) => void
  highlight: number
  setHighlight: (index: number) => void
  onSelect: (result: SearchResult) => void
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  t: (key: string) => string
  collapsed: boolean
  onCollapsedClick: () => void
}

export default function SidebarSearch({
  value, onChange,
  results, showDropdown, setShowDropdown,
  highlight, setHighlight,
  onSelect, onKeyDown, t,
  collapsed, onCollapsedClick,
}: SidebarSearchProps) {
  // Collapsed rail — the full input has no room, so surface a search icon
  // button that opens the command palette. Keeps search reachable instead
  // of vanishing entirely when the sidebar is collapsed.
  if (collapsed) {
    return (
      <div className="sidebar-search" style={{ padding: '0.5rem 0.25rem', marginBottom: '0.5rem' }}>
        <button
          type="button"
          className="nav-item"
          onClick={onCollapsedClick}
          data-sb-tip={t('search')}
          aria-label={t('search')}
          style={{ width: '100%', border: 'none', background: 'none', cursor: 'pointer', justifyContent: 'center' }}
        >
          <span className="nav-icon"><Search size={16} aria-hidden="true" /></span>
        </button>
      </div>
    )
  }

  return (
    <div className="sidebar-search" style={{ position: 'relative', padding: '0.75rem 0.75rem', marginBottom: '0.5rem' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder={t('filterSearch')}
          value={value}
          onChange={e => { onChange(e.target.value); setHighlight(-1) }}
          onFocus={() => value && setShowDropdown(true)}
          onKeyDown={onKeyDown}
          aria-label={t('filterSearch')}
          role="combobox"
          aria-expanded={showDropdown && results.length > 0}
          aria-autocomplete="list"
          aria-controls="search-listbox"
          aria-activedescendant={highlight >= 0 ? `search-option-${highlight}` : undefined}
          style={{
            width: '100%', padding: '8px 12px', fontSize: 'var(--text-caption)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius)', color: 'var(--text-primary)',
            boxSizing: 'border-box',
          }}
        />
        {showDropdown && results.length > 0 && (
          <div
            id="search-listbox"
            role="listbox"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius)', maxHeight: '300px', overflowY: 'auto',
              zIndex: 'var(--z-modal)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {results.map((result, i) => (
              <div
                key={i}
                id={`search-option-${i}`}
                role="option"
                aria-selected={i === highlight}
                onClick={() => onSelect(result)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(result) } }}
                style={{
                  padding: '8px 12px', fontSize: 'var(--text-caption)', cursor: 'pointer',
                  borderBottom: i < results.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  transition: 'background 0.15s',
                  display: 'flex', alignItems: 'center',
                  background: i === highlight ? 'var(--bg-elevated)' : 'transparent',
                }}
                onMouseEnter={e => { setHighlight(i); e.currentTarget.style.background = 'var(--bg-elevated)' }}
                onMouseLeave={e => { e.currentTarget.style.background = i === highlight ? 'var(--bg-elevated)' : 'transparent' }}
              >
                <span style={{ marginRight: '0.5rem' }}>{result.icon}</span>
                {result.name}
                <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{result.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
